/**
 * SA-1328 (spec §4): where the record page lands after a task is done — the
 * pure half. `landingTarget` names the thing to bring on screen (the current
 * card on the map, the NOW or Closed row in the list, else the band);
 * `landingScrollTop` is the scroll position that puts it in the middle of the
 * space under the sticky strip.
 */
import type { RecordProcessRead } from '@/types/process'
import type { PassView } from './passes'

export const LANDING_GAP_PX = 12

export type LandingTarget = { kind: 'step'; stepId: string } | { kind: 'row'; key: string } | { kind: 'band' }

/** Map: the current step while running and drawn (`drawn` = the step ids the map draws), else the band. List: the current row, else the closed row, else the band. */
export function landingTarget(record: Pick<RecordProcessRead, 'status' | 'current_step'>, mode: 'map' | 'list', view: PassView | null, drawn: ReadonlySet<string>): LandingTarget {
  if (mode === 'map') {
    return record.status === 'running' && record.current_step && drawn.has(record.current_step.id)
      ? { kind: 'step', stepId: record.current_step.id }
      : { kind: 'band' }
  }
  if (!view) return { kind: 'band' }
  const keys = view.rows.flatMap((r) => (r.kind === 'entry' ? [r.entry.key] : []))
  const now = record.current_step ? `${record.current_step.id}#current` : null
  if (now && keys.includes(now)) return { kind: 'row', key: now }
  if (keys.includes('closed')) return { kind: 'row', key: 'closed' }
  return { kind: 'band' }
}

export interface LandingMeasure {
  scrollTop: number
  scrollerTop: number
  scrollerHeight: number
  stripBottom: number | null
  targetTop: number
  targetHeight: number
  align: 'center' | 'start'
}

/** The scroll top that centers the target in the space below the strip (+ gap), or lands its top there when it is taller than that space or `align` is `start`; never below 0. */
export function landingScrollTop(m: LandingMeasure): number {
  const offset = m.stripBottom === null ? 0 : m.stripBottom - m.scrollerTop + LANDING_GAP_PX
  const space = m.scrollerHeight - offset
  const at = m.scrollTop + (m.targetTop - m.scrollerTop)
  const top = m.align === 'center' && m.targetHeight < space ? at - offset - (space - m.targetHeight) / 2 : at - offset
  return Math.max(0, top)
}
